import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import type {
  CheckoutDiscountConfig,
  UpdateCheckoutDiscountConfigRequest,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

const CONFIG_QUERY_KEY = ["checkout", "discount-config"] as const;

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

/** Stored fraction (0.55) → percent string for the input ("55"). */
function fractionToPercent(fraction: number): string {
  return String(parseFloat((fraction * 100).toFixed(2)));
}

interface FormState {
  couponCode: string;
  /** Percent off, as typed by the user (e.g. "55"). */
  discountPercent: string;
  checkoutPageId: string;
}

function toFormState(config: CheckoutDiscountConfig): FormState {
  return {
    couponCode: config.couponCode,
    discountPercent: fractionToPercent(config.discountFraction),
    checkoutPageId: config.checkoutPageId,
  };
}

export default function CheckoutDiscountPricesPage() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: api.checkout.getDiscountConfig,
  });

  const [form, setForm] = useState<FormState | null>(null);

  // Initialise the form once the config loads.
  useEffect(() => {
    if (configQuery.data) setForm(toFormState(configQuery.data.config));
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (body: UpdateCheckoutDiscountConfigRequest) =>
      api.checkout.updateDiscountConfig(body),
    onSuccess: (data) => {
      queryClient.setQueryData(CONFIG_QUERY_KEY, data);
      setForm(toFormState(data.config));
      toast.success("Discount config saved");
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;

    const couponCode = form.couponCode.trim();
    const checkoutPageId = form.checkoutPageId.trim();
    const percent = Number(form.discountPercent);

    if (!couponCode) return toast.error("Coupon code is required");
    if (!checkoutPageId) return toast.error("Checkout page is required");
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      return toast.error("Discount must be between 0 and 100");
    }

    // Percent → decimal fraction (0.55), rounded to avoid float noise.
    const discountFraction = parseFloat((percent / 100).toFixed(4));
    saveMutation.mutate({ couponCode, discountFraction, checkoutPageId });
  };

  const config = configQuery.data?.config;
  const updatedLabel = config?.updatedAt
    ? `Last saved ${new Date(config.updatedAt).toLocaleString()}${
        config.updatedByEmail ? ` by ${config.updatedByEmail}` : ""
      }`
    : "Not saved yet — showing defaults";

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/checkout"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Checkout
        </Link>
        <h1 className="text-2xl font-bold">Update Discount Prices</h1>
        <p className="text-sm text-muted-foreground">
          Campaign checkout coupon, discount, and analytics page identifier.
        </p>
      </div>

      {configQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : configQuery.isError ? (
        <p className="text-sm text-destructive">
          {describeError(configQuery.error)}
        </p>
      ) : form ? (
        <form onSubmit={handleSubmit} className="max-w-md space-y-5">
          <div className="space-y-2">
            <Label htmlFor="couponCode">Coupon code</Label>
            <Input
              id="couponCode"
              value={form.couponCode}
              onChange={(e) => setForm({ ...form, couponCode: e.target.value })}
              placeholder="INTRO55"
            />
            <p className="text-xs text-muted-foreground">
              Used across Stripe, FastSpring, query params, and landing pages.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="discountPercent">Discount (% off)</Label>
            <div className="relative">
              <Input
                id="discountPercent"
                type="number"
                min={0}
                max={100}
                step="any"
                value={form.discountPercent}
                onChange={(e) =>
                  setForm({ ...form, discountPercent: e.target.value })
                }
                placeholder="55"
                className="pr-8"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Percent off the list price. Stored as a decimal fraction
              (55% → 0.55).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="checkoutPageId">Checkout page</Label>
            <Input
              id="checkoutPageId"
              value={form.checkoutPageId}
              onChange={(e) =>
                setForm({ ...form, checkoutPageId: e.target.value })
              }
              placeholder="PrimeDay"
            />
            <p className="text-xs text-muted-foreground">
              Identifier sent to analytics on every checkout event.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
            <span className="text-xs text-muted-foreground">{updatedLabel}</span>
          </div>
        </form>
      ) : null}
    </div>
  );
}
